import { createRef } from 'react';
import { tMessageSearchContext } from './MessageSearch.types';

/**
 * 500 ms, not the 400 ms the inbox uses, and not per-keystroke.
 *
 * Measured: searching on every keystroke moves this query from 3.1% to 12.4%
 * of the database reader's average active sessions — four times the load for a
 * feel-good behaviour. 500 ms is the measured floor, not a preference. If you
 * are about to lower this back to 400, re-run the capacity baseline first.
 */
export const SEARCH_DEBOUNCE_MS = 500;

/**
 * Minimum length of the trimmed query, counted in runes (Unicode code points),
 * never bytes or UTF-16 units. bff-web applies the same guard on the same
 * trimmed value, and the two must agree: a 1–2 rune query returns 200 with an
 * empty `messages` array, which the client cannot tell apart from "no results".
 */
export const MIN_SEARCH_RUNES = 3;

/**
 * `since` is always sent, defaulting to this window.
 *
 * Omitting it is the wrong-plan path: without a time window the query reads
 * 1.29 GiB and takes 305 ms. With it, the partition pruning holds.
 */
export const SEARCH_WINDOW_DAYS = 30;

/**
 * The in-chat search's window (one contact, SRE-3328): three months, the
 * product scope chosen for "Buscar en esta conversación". chats-service clamps
 * `since` to the same floor anyway; this is what the client asks for and what
 * the copy ("últimos 3 meses") is derived from, so the two cannot disagree.
 */
export const LEAD_SEARCH_WINDOW_DAYS = 90;

/** The server clamps `limit` to [1, 50]; do not advertise more than 50. */
export const SEARCH_PAGE_SIZE = 20;

/**
 * Cap on one identity batch. bff-web truncates at the same number, and the point
 * of both is the same: one call per page, never one call per row.
 */
export const MAX_IDENTITY_HANDLES = 50;

export const initialState: tMessageSearchContext = {
  inputValue: '',
  setInputValue: () => {},
  searchTerm: '',
  isQueryTooShort: true,
  isNegationOnly: false,
  messages: [],
  answered: null,
  loading: false,
  loadingMore: false,
  isLastPage: false,
  namesByLeadId: {},
  namesByHandle: {},
  searchInputRef: createRef<HTMLInputElement>(),
  getMoreMessages: () => Promise.resolve(),
  trackResultOpened: () => {},
};
